// Settlements (liquidaciones) API. See apps/api/src/routes/settlements.ts for
// the exact contract this mirrors — a PaymentSettlement batches many
// SalePayment rows of a settlesInBatch payment method (e.g. ClubCred) into
// one operator-confirmed bank transfer. REGLA CENTRAL: creating/reverting a
// settlement never writes netReceivedCents/commissionCents back onto a
// SalePayment/Sale — only settlementId moves. The real net only ever exists
// here, at the batch level (grossCents/transferredCents/commissionCents).

import { api } from "../../lib/api";

export type SettlementPendingSalePayment = {
  sourceType: "sale_payment";
  salePaymentId: number;
  saleId: number;
  saleCreatedAt: string;
  amountCents: number;
  methodName: string;
  // True when the sale that produced this payment was split across more
  // than one payment method — amountCents is only this method's portion.
  isSplitSale: boolean;
};

export type SettlementPendingAccountCollection = {
  sourceType: "account_settlement";
  accountSettlementId: number;
  customerId: number;
  occurredAt: string;
  amountCents: number;
  methodName: string;
};

export type SettlementPendingPayment = SettlementPendingSalePayment | SettlementPendingAccountCollection;

export type SettlementStatus = "active" | "reverted";

export type SettlementSummary = {
  id: number;
  paymentMethodId: number;
  methodName: string;
  settledOn: string;
  grossCents: number;
  transferredCents: number;
  commissionCents: number;
  operatorId: number;
  note: string | null;
  status: SettlementStatus;
  revertedAt: string | null;
  revertedById: number | null;
  createdAt: string;
};

export type SettlementDetailSalePayment = {
  sourceType: "sale_payment";
  salePaymentId: number;
  saleId: number;
  saleCreatedAt: string;
  saleStatus: "active" | "voided" | null;
  amountCents: number;
};

export type SettlementDetailAccountCollection = {
  sourceType: "account_settlement";
  accountSettlementId: number;
  customerId: number;
  occurredAt: string;
  amountCents: number;
};

export type SettlementDetailPayment = SettlementDetailSalePayment | SettlementDetailAccountCollection;

export type SettlementDetail = SettlementSummary & {
  payments: SettlementDetailPayment[];
};

export type CreateSettlementInput = {
  /**
   * Short-lived token returned by the API after an operator reviews an
   * unusually high amount. It authorizes this exact request body only.
   */
  moneyConfirmationToken?: string;
  paymentMethodId: number;
  // YYYY-MM-DD, día en que llegó la transferencia.
  settledOn: string;
  transferredCents: number;
  salePaymentIds: number[];
  accountSettlementIds: number[];
  note?: string;
};

export const settlementsApi = {
  // GET /api/settlements/pending?paymentMethodId=&from=&to=
  // from/to are full ISO datetimes (the backend validates z.string().datetime()).
  pending: (params: { paymentMethodId?: number; from?: string; to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.paymentMethodId) q.set("paymentMethodId", String(params.paymentMethodId));
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    const qs = q.toString();
    return api<SettlementPendingPayment[]>(`/api/settlements/pending${qs ? `?${qs}` : ""}`);
  },
  create: (body: CreateSettlementInput) =>
    api<SettlementDetail>("/api/settlements", { method: "POST", body }),
  // GET /api/settlements?from=&to= (historial, más reciente primero)
  list: (params: { from?: string; to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    const qs = q.toString();
    return api<SettlementSummary[]>(`/api/settlements${qs ? `?${qs}` : ""}`);
  },
  get: (id: number) => api<SettlementDetail>(`/api/settlements/${id}`),
  revert: (id: number) => api<SettlementDetail>(`/api/settlements/${id}/revert`, { method: "POST", body: {} }),
};
